import { Box } from 'components/Box';
import { AnchorButton } from 'components/Button/AnchorButton';
import { CmsSocialButtons } from 'components/CmsSocialButtons';
import { FilestackImage } from 'components/FilestackImage/FilestackImage';
import { ConditionalLink, Link } from 'components/Link';
import { Body } from 'components/Text/Body';
import { BodySmall } from 'components/Text/BodySmall';
import { H1 } from 'components/Text/H1';
import { H4 } from 'components/Text/H4';
import { HeadingSmall } from 'components/Text/HeadingSmall';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import { CmsAmpContentBlock } from 'modules/cms/blocks/Amp/CmsAmpContentBlock';
import { CmsRecommendedJobsBlock } from 'modules/cms/blocks/RecommendedJobs/CmsRecommendedJobsBlock';
import {
  CmsBodyContainer,
  CmsBodyContainerWidth,
} from 'modules/cms/components/Body/CmsBodyContainer';
import {
  getContentOnlySearchResults,
  getContentPageUrl,
  getIsContentFullWidthLayout,
} from 'modules/cms/helpers';
import { useCmsApiPageHomePageMeta } from 'modules/cms/hooks';
import { colors, gutterWidth, maxWidth } from 'theme/theme';
import { humanDateTime } from 'utils/date';
import { getRgbaString } from 'utils/style';

type Props = {
  content: CmsApiPage;
  searchMode: boolean;
};

export function CmsAmpContentPage({ content, searchMode }: Props) {
  const pageUrl = getContentPageUrl({ content });
  const { homePageUrl } = useCmsApiPageHomePageMeta({
    cmsApiPage: content,
  });
  const fullWidthLayout = getIsContentFullWidthLayout({ content });

  const resultsPage = 0;

  const onlySearchResults = getContentOnlySearchResults({ content });
  if (onlySearchResults) {
    return null;
  }

  return !content.heroSearch?.onlyResults ? (
    <Box
      position="relative"
      maxWidth={[...maxWidth]}
      py={[gutterWidth * 1.25, null, null, gutterWidth * 2]}
      m="0 auto"
      data-qa-id="amp-content"
      textAlign={content.type === 'PAGE' ? 'center' : undefined}
    >
      <CmsBodyContainer $width={CmsBodyContainerWidth.NARROW_LAYOUT}>
        {searchMode ? (
          <>
            <amp-list
              id="article_results_list"
              layout="fixed-height"
              height="450"
              src={`/data/website/search/posts/${content.subsite.id}/${CURRENT_LOCALE}/${resultsPage}`}
              items="results.hits"
              binding="always"
            >
              <template type="amp-mustache">
                <Box
                  display="inline-block"
                  width={[1, 1 / 2, 1 / 3]}
                  verticalAlign="top"
                  maxWidth={[320, 360]}
                  mx="auto"
                  px={[0, gutterWidth / 2]}
                  py={gutterWidth / 2}
                  textAlign="left"
                >
                  <Box
                    component="a"
                    href="/{{url}}"
                    display="block"
                    mb={24}
                    borderRadius={8}
                    overflow="hidden"
                    bg={colors.inputGrey}
                  >
                    <div data-style="padding-bottom: 56.25%; width: 100%; height: 100%; background-size: cover; background-position: center; background-image: url(https://cdn.filestackcontent.com/quality=value:70/cache=expiry:31536000/compress/no_metadata/{{thumbnail.handle}})" />
                  </Box>
                  <H4 mt={0} mb={16} lineHeight={1.375}>
                    <a href="/{{url}}">{'{{title}}'}</a>
                  </H4>
                </Box>
              </template>
            </amp-list>
            <Box
              position="relative"
              zIndex={1}
              pt={100}
              mt={-100}
              backgroundImage={`linear-gradient(to top, ${
                colors.backgroundGrey
              } 0%, ${colors.backgroundGrey} 50%, rgba(${getRgbaString(
                colors.backgroundGrey,
                0,
              )}) 100%)`}
            >
              <AnchorButton
                size="large"
                href={`/${pageUrl}?pq=`}
                variant="secondary"
              >
                {getText('Show More')}
              </AnchorButton>
            </Box>
          </>
        ) : (
          <>
            <H1>{content.title}</H1>
            {(content.author || (content.postedDate && content.showDate)) && (
              <Box mt={4}>
                {content.author?.name && (
                  <H4 data-qa-id="author-name" m={0} lineHeight={1}>
                    <ConditionalLink
                      data-qa-id="author-name-link"
                      link={
                        homePageUrl && content.author.slug
                          ? `/${homePageUrl}?author.slug=${content.author.slug}&pq=`
                          : ''
                      }
                    >
                      <Body lineHeight={1.33}>{content.author.name}</Body>
                    </ConditionalLink>
                  </H4>
                )}
                {content.showDate && (
                  // @ts-expect-error TS(2322): Type '{ children: string; tag: string; dateTime: s... Remove this comment to see the full error message
                  <BodySmall tag="time" dateTime={content.postedDate}>
                    {humanDateTime(content.postedDate)}
                  </BodySmall>
                )}
              </Box>
            )}
            {content.headerImage && (
              <Box mt={4}>
                <FilestackImage
                  data={content.headerImage}
                  alt={content.headerImage.altText || content.title}
                  height={400}
                  width={fullWidthLayout ? 1152 : 952}
                />
              </Box>
            )}
            {content.contentBlocks.slice(0, 10).map((block, index) =>
              block.type === 'ANCHOR' ? (
                <div
                  id={`content-anchor-${block.data.anchor}`}
                  key={block.id}
                />
              ) : (
                <Box
                  my={[gutterWidth * 1.25, null, null, gutterWidth * 2]}
                  key={block.id}
                  id={`cms-block-${block.id}`}
                  display={
                    block.type === 'FREE_CODE'
                      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (!block.data.isVisible as any)
                      : undefined
                  }
                >
                  <CmsAmpContentBlock
                    content={content}
                    block={block}
                    blockIndex={index}
                  />
                </Box>
              ),
            )}

            {content.contentBlocks.length > 10 && (
              <Box mt={64}>
                <AnchorButton href={`/${pageUrl}`}>
                  {getText('View Full Page')}
                </AnchorButton>
              </Box>
            )}
            {content.hasRecommendedJobs && (
              <CmsRecommendedJobsBlock
                data={{
                  headline: content.recommendedJobsHeadline || null,
                  layout: 'REGULAR',
                  jobFunction: null,
                  areaOfFocus: null,
                }}
                fromCheckbox
              />
            )}
            {(content.hasSocialSharing ||
              (content.subsite.canSubscribe && content.type === 'POST') ||
              (content.tags && content.tags.length > 0)) && (
              <Box mt={64} id="subscribe">
                {(content.hasSocialSharing ||
                  (content.subsite.canSubscribe &&
                    content.type === 'POST')) && (
                  <Box classes={['flex']} m="-6px" mt={32}>
                    {content.hasSocialSharing && (
                      <CmsSocialButtons
                        content={content}
                        message={content.metaDescription}
                        title={content.title}
                        subsite={content.subsite.publicName}
                      />
                    )}
                    {content.subsite.canSubscribe &&
                      content.type === 'POST' && (
                        <Box m="6px">
                          <AnchorButton
                            size="small"
                            href={`${pageUrl}?subscribe=true#subscribe`}
                            variant="secondary"
                          >
                            {getText('Subscribe')}
                          </AnchorButton>
                        </Box>
                      )}
                  </Box>
                )}
                {content.tags && content.tags.length > 0 && (
                  <Box mt={32} classes={['flex']} component={HeadingSmall}>
                    {getText('Tags: ')}
                    {content.tags.map((tag) => (
                      <Box ml={12}>
                        <Box
                          component={homePageUrl ? Link : undefined}
                          // @ts-expect-error fix me
                          href={
                            homePageUrl
                              ? `/${homePageUrl}?tags.slug=${tag.slug}&pq=`
                              : undefined
                          }
                        >
                          {tag.tag}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </CmsBodyContainer>
    </Box>
  ) : null;
}
